<template>
  <div class="chip-scroll">
    <span v-if="showLeft" class="chip-scroll__fade chip-scroll__fade--left" aria-hidden="true"></span>
    <button
      v-if="showLeft"
      type="button"
      class="chip-scroll__arrow chip-scroll__arrow--left"
      :aria-label="leftLabel"
      @click="scrollByStep(-1)"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="15 18 9 12 15 6"></polyline>
      </svg>
    </button>

    <div ref="viewport" class="chip-scroll__viewport" role="group" :aria-label="label" @scroll="update">
      <slot />
    </div>

    <span v-if="showRight" class="chip-scroll__fade chip-scroll__fade--right" aria-hidden="true"></span>
    <button
      v-if="showRight"
      type="button"
      class="chip-scroll__arrow chip-scroll__arrow--right"
      :aria-label="rightLabel"
      @click="scrollByStep(1)"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
    </button>
  </div>
</template>

<script>
const VISIBILITY_THRESHOLD = 4;

export default {
  name: "ChipScroll",
  props: {
    label: { type: String, default: "" },
    leftLabel: { type: String, default: "Ver filtros anteriores" },
    rightLabel: { type: String, default: "Ver mais filtros" },
    scrollStep: { type: Number, default: 0 },
  },
  data() {
    return {
      showLeft: false,
      showRight: false,
    };
  },
  mounted() {
    this.mutationObserver = new MutationObserver(() => this.refresh());
    if (this.$refs.viewport) {
      this.mutationObserver.observe(this.$refs.viewport, {
        childList: true,
        subtree: true,
      });
    }
    window.addEventListener("resize", this.refresh);
    this.refresh();
  },
  updated() {
    this.refresh();
  },
  beforeDestroy() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    window.removeEventListener("resize", this.refresh);
  },
  methods: {
    refresh() {
      this.$nextTick(() => {
        const viewport = this.$refs.viewport;
        if (!viewport) return;
        const remaining = viewport.scrollWidth - viewport.clientWidth - viewport.scrollLeft;
        this.showLeft = viewport.scrollLeft > VISIBILITY_THRESHOLD;
        this.showRight = remaining > VISIBILITY_THRESHOLD;
      });
    },
    update() {
      const viewport = this.$refs.viewport;
      if (!viewport) return;
      const remaining = viewport.scrollWidth - viewport.clientWidth - viewport.scrollLeft;
      this.showLeft = viewport.scrollLeft > VISIBILITY_THRESHOLD;
      this.showRight = remaining > VISIBILITY_THRESHOLD;
    },
    scrollByStep(direction) {
      const viewport = this.$refs.viewport;
      if (!viewport) return;
      const step = this.scrollStep > 0
        ? this.scrollStep
        : Math.max(viewport.clientWidth * 0.75, 160);
      viewport.scrollBy({ left: direction * step, behavior: "smooth" });
    },
  },
};
</script>

<style scoped>
.chip-scroll {
  position: relative;
  display: flex;
  align-items: center;
  margin-top: 12px;
  padding: 2px 0;
}

.chip-scroll__viewport {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
  padding: 2px;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.chip-scroll__viewport::-webkit-scrollbar {
  display: none;
}

.chip-scroll__arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 4;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 50%;
  background: rgba(37, 23, 56, 0.92);
  color: #b7a3cb;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
  transition: background 0.2s ease, color 0.2s ease, transform 0.15s ease;
}

.chip-scroll__arrow:hover {
  background: #7a23bd;
  color: #fff;
}

.chip-scroll__arrow:active {
  transform: translateY(-50%) scale(0.94);
}

.chip-scroll__arrow:focus-visible {
  outline: 2px solid rgba(255, 215, 0, 0.9);
  outline-offset: 2px;
}

.chip-scroll__arrow svg {
  width: 15px;
  height: 15px;
}

.chip-scroll__arrow--left {
  left: 0;
}

.chip-scroll__arrow--right {
  right: 0;
}

.chip-scroll__fade {
  position: absolute;
  top: 2px;
  bottom: 2px;
  width: 34px;
  z-index: 3;
  pointer-events: none;
}

.chip-scroll__fade--left {
  left: 0;
  background: linear-gradient(90deg, rgba(21, 9, 46, 0.92) 0%, transparent 100%);
}

.chip-scroll__fade--right {
  right: 0;
  background: linear-gradient(270deg, rgba(21, 9, 46, 0.92) 0%, transparent 100%);
}

@media (max-width: 499px) {
  .chip-scroll__arrow {
    width: 24px;
    height: 24px;
  }

  .chip-scroll__arrow svg {
    width: 13px;
    height: 13px;
  }

  .chip-scroll__fade {
    width: 28px;
  }
}
</style>